import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, Redirect, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "antd";
import { ReloadOutlined, ClearOutlined } from "@ant-design/icons";
import * as ACTIONS from "../../../constants/actions";
import AuthorisedFor from "../../../utils/authorisedFor";
import SamplingTrackingDetailed from "../../../components/Backoffice/SamplingTrackingDetailed";
import CommoditySamplingDetailed from "../../../components/Backoffice/CommoditySamplingDetailed";
import { useAuth } from "../../../contexts/auth";
import routes from "../../../../routes";
import TestingAnalyticsDetailed from "../../../components/Backoffice/TestingAnalyticsDetailed";
import TATMonitoringDetailed from "../../../components/Backoffice/TATDetailed";
import DashboardFilter from "./DashboardFilters";
import { selectGeographyData } from "../../../components/Backoffice/SamplingCoverageDetailed/selectors";
import * as selectors from "./DashboardFilters/selectors";
import GeographyAnalyticsTable from "../../../components/Backoffice/SamplingCoverageDetailed";
import RegretCaseTrackingPage from "../../../components/Backoffice/RegretCaseDetailed";
import DetailedParameterAnalytics from "../../../components/Backoffice/ParameterComplianceDetailed";

const { backOffice } = routes;

export default function DetailsRouter() {
  const geographyData = useSelector(selectGeographyData);
  const history = useHistory();
  const auth = useAuth();
  const isCompanyAdmin = auth.isCompanyAdmin;
  const homeRedirect = isCompanyAdmin
    ? backOffice.orders.list
    : backOffice.dashboard;

  const refreshIntervalRef = useRef(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const type = params.get("type");
  const filtersFromLocation = location.state?.filters || {};
  const rawProjects = useSelector(selectors.selectRawProjects);

  // Auto-refresh state
  const [refreshKey, setRefreshKey] = useState(0);

  // Get filters from URL parameters for persistence on refresh
  const getFiltersFromURL = () => {
    const urlParams = new URLSearchParams(location.search);
    return {
      companyIds: urlParams.getAll("companyIds").filter(Boolean),
      projectCodeIds: urlParams.getAll("projectCodeIds").filter(Boolean),
      selectedState: urlParams.getAll("state").filter(Boolean),
      sortDescending: urlParams.get("sortDescending") !== "false", // defaults to true
      selectedCommodities: urlParams.getAll("commodity").filter(Boolean),
      selectedTestingTypes: urlParams.getAll("testingType").filter(Boolean),
    };
  };

  const urlFilters = getFiltersFromURL();

  // Initialize state with URL filters first, then location state, then empty arrays
  const [selectedClients, setSelectedClients] = useState(
    urlFilters.companyIds.length > 0
      ? urlFilters.companyIds
      : filtersFromLocation.companyIds || []
  );

  const [selectedProjects, setSelectedProjects] = useState(
    urlFilters.projectCodeIds.length > 0
      ? urlFilters.projectCodeIds
      : filtersFromLocation.projectCodeIds || []
  );

  const [selectedState, setSelectedState] = useState(
    urlFilters.selectedState.length > 0 ? urlFilters.selectedState : []
  );

  const [sortDescending, setSortDescending] = useState(
    urlFilters.sortDescending
  );

  // New state for param page filters
  const [selectedCommodities, setSelectedCommodities] = useState(
    urlFilters.selectedCommodities.length > 0
      ? urlFilters.selectedCommodities
      : []
  );

  const [selectedTestingTypes, setSelectedTestingTypes] = useState(
    urlFilters.selectedTestingTypes.length > 0
      ? urlFilters.selectedTestingTypes
      : []
  );

  const [parameterRequest, setParameterRequest] = useState(
    filtersFromLocation.parameterRequest || "overall"
  );

  // Auto-refresh every 5 minutes
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Manual refresh handler
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Reset filters handler
  const handleResetFilters = () => {
    setSelectedClients([]);
    setSelectedProjects([]);
    setSelectedState([]);
    setSortDescending(true);
    setSelectedCommodities([]);
    setSelectedTestingTypes([]);
  };

  // Update URL whenever filters change to persist them
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Clear existing filter params
    params.delete("companyIds");
    params.delete("projectCodeIds");
    params.delete("state");
    params.delete("sortDescending");
    params.delete("commodity");
    params.delete("testingType");

    // Add current filter values
    selectedClients.forEach((id) => params.append("companyIds", id));
    selectedProjects.forEach((id) => params.append("projectCodeIds", id));
    selectedState.forEach((state) => params.append("state", state));
    params.set("sortDescending", sortDescending.toString());
    
    // Add param page specific filters
    if (type === "param") {
      selectedCommodities.forEach((commodity) => 
        params.append("commodity", commodity)
      );
      selectedTestingTypes.forEach((testingType) => 
        params.append("testingType", testingType)
      );
    }

    // Only update URL if it's different from current
    const newSearch = params.toString();
    if (newSearch !== location.search.substring(1)) {
      history.replace({
        pathname: location.pathname,
        search: newSearch,
        state: location.state,
      });
    }
  }, [
    selectedClients,
    selectedProjects,
    selectedState,
    sortDescending,
    selectedCommodities,
    selectedTestingTypes,
    type,
    history,
    location.pathname,
    location.search,
    location.state,
  ]);

  // Smart deselect logic using the helper function
  const handleClientChange = (newSelectedClients) => {
    setSelectedClients(newSelectedClients);
    // Smart deselect projects
    if (newSelectedClients.length === 0) {
      setSelectedProjects([]);
      setSelectedState([]);
    } else {
      const validProjects = selectors.getValidProjects(
        selectedProjects,
        newSelectedClients,
        rawProjects
      );
      setSelectedProjects(validProjects);
    }
  };

  const handleProjectChange = (newSelectedProjects) => {
    setSelectedProjects(newSelectedProjects);
  };

  // Handlers for new param page filters
  const handleCommodityChange = (newSelectedCommodities) => {
    setSelectedCommodities(newSelectedCommodities);
  };

  const handleTestingTypeChange = (newSelectedTestingTypes) => {
    setSelectedTestingTypes(newSelectedTestingTypes);
  };

  const handleParameterRequest = (value) => {
    setParameterRequest(value);
  };

  const showStateDropdown = type === "map";
  const showRegretDropdown = type === "regret";
  const showParamDropdowns = type === "param";

  const stateOptions = useMemo(() => {
    if (!Array.isArray(geographyData)) return [];
    const uniqueStates = Array.from(
      new Set(geographyData.map((item) => item.state).filter(Boolean))
    );
    uniqueStates.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    return uniqueStates.map((state) => ({
      label: state,
      value: state,
    }));
  }, [geographyData]);

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(
    () => ({
      companyIds: selectedClients,
      projectCodeIds: selectedProjects,
      selectedState,
      sortDescending,
      selectedCommodities,
      selectedTestingTypes,
      parameterRequest
    }),
    [
      selectedClients,
      selectedProjects,
      selectedState,
      sortDescending,
      selectedCommodities,
      selectedTestingTypes,
      parameterRequest
    ]
  );

  const getActionAndComponent = (type) => {
    switch (type) {
      case "sampling":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_SAMPLING,
          component: <SamplingTrackingDetailed key={`sampling-${refreshKey}`} filters={filters} />,
        };
      case "commodity":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_COMMODITY,
          component: <CommoditySamplingDetailed key={`commodity-${refreshKey}`} filters={filters} />,
        };
      case "testing":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_TESTING,
          component: <TestingAnalyticsDetailed key={`testing-${refreshKey}`} filters={filters} />,
        };
      case "tat":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_TAT,
          component: <TATMonitoringDetailed key={`tat-${refreshKey}`} filters={filters} />,
        };
      case "map":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_SAMPLING_COVERAGE,
          component: <GeographyAnalyticsTable key={`map-${refreshKey}`} filters={filters} />,
        };
      case "regret":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_REGRET,
          component: <RegretCaseTrackingPage key={`regret-${refreshKey}`} />,
        };
      case "param":
        return {
          action: ACTIONS.BO_DASHBOARD_NEW_COMPLIANCE,
          component: <DetailedParameterAnalytics key={`param-${refreshKey}`} filters={filters} />,
        };
      default:
        return null;
    }
  };

  const result = getActionAndComponent(type);
  if (!result) {
    return <div>Invalid detail type</div>;
  }

  return (
    <AuthorisedFor
      action={result.action}
      yes={
        <div style={{ padding: 24 }}>
          {/* Refresh and Reset buttons */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={handleRefresh}
              icon={<ReloadOutlined />}
            >
              Refresh
            </Button>
            <Button
              onClick={handleResetFilters}
              icon={<ClearOutlined />}
            >
              Reset Filters
            </Button>
          </div>

          <DashboardFilter
            selectedClients={selectedClients}
            selectedProjects={selectedProjects}
            handleClientChange={handleClientChange}
            handleProjectChange={handleProjectChange}
            extraStateOptions={type === "map" ? stateOptions : []}
            selectedState={type === "map" ? selectedState : undefined}
            handleStateChange={type === "map" ? setSelectedState : undefined}
            showStateDropdown={showStateDropdown}
            showCompletionSort={type === "map"}
            onToggleSort={() => setSortDescending((prev) => !prev)}
            isSortedDescending={sortDescending}
            showRegretDropdown={showRegretDropdown}
            selectedCommodities={selectedCommodities}
            handleCommodityChange={handleCommodityChange}
            selectedTestingTypes={selectedTestingTypes}
            handleTestingTypeChange={handleTestingTypeChange}
            showParamDropdowns={showParamDropdowns}
            currentView={type === "param" ? "compliance" : null}
            parameterRequest={parameterRequest}
            handleParameterRequest={handleParameterRequest}
          />
          <div style={{ marginTop: 24 }}>{result.component}</div>
        </div>
      }
      no={<Redirect to={homeRedirect} />}
    />
  );
}